/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @nx/enforce-module-boundaries */
import React, { useContext, useMemo, useCallback, useRef } from 'react';
import { renderedData } from '../../organisms/OSearchResults/searchResultsMainComponent';
import { useSearchResultsStore } from '../../modules/store/searchResultsStore';
import { useMarriottAIPreviewStore } from '../../store';
import { SearchResultContext, MapViewContext, SearchFilterContext } from '../../organisms/OSearchResults/index.context';
import { usePageContext } from '@marriott/mi-shop-components';
import { getLabelText } from '../mapView/mapHelper';
import { ChatMapView } from './ChatMapView';
import { ChatMapMarker } from './ChatMapView.types';

/**
 * Custom hook to selectively consume only map-related values from renderedData context.
 * This prevents re-renders when unrelated context values change (e.g., jsonContent, recentlyViewed, etc.)
 */
const useMapRenderedData = () => {
  const ctx = useContext(renderedData) as any;

  // Extract only the primitive values we need
  const isStaticMap = ctx.isStaticMap;
  const isMapLoading = ctx.isMapLoading;
  const isErrorState = ctx.isErrorState;
  const isSpecialRateSearch = ctx.isSpecialRateSearch;

  // Memoize based on individual primitive values, not the entire context object
  return useMemo(
    () => ({
      isStaticMap: isStaticMap ?? false,
      isMapLoading: isMapLoading ?? false,
      isErrorState: isErrorState ?? true,
      isSpecialRateSearch,
    }),
    [isStaticMap, isMapLoading, isErrorState, isSpecialRateSearch]
  );
};

/**
 * Custom hook to selectively consume search result edges.
 * Only triggers re-render when edges array reference changes.
 */
const useSearchResultEdges = () => {
  const { searchResult } = useContext(SearchResultContext);
  return searchResult?.edges ?? [];
};

/**
 * Custom hook to get hovered hotel ID from MapViewContext.
 * Memoizes the extraction to prevent unnecessary re-renders.
 */
const useHoveredHotelId = () => {
  const { selectedHotel } = useContext(MapViewContext);
  return useMemo(() => {
    if (!selectedHotel || selectedHotel.length === 0) return null;
    const hotel = selectedHotel[0] as { marsha?: string; code?: string; propertyId?: string } | undefined;
    return hotel?.marsha || hotel?.code || hotel?.propertyId || null;
  }, [selectedHotel]);
};

/**
 * Custom hook to get isSinglePropertyResponse from MapViewContext.
 */
const useIsSinglePropertyResponse = () => {
  const { isSinglePropertyResponse } = useContext(MapViewContext);
  return isSinglePropertyResponse ?? false;
};

/**
 * Custom hook to extract only the needed values from searchQueryState.
 * Returns stable primitive values instead of the entire object reference.
 */
const useSearchQueryValues = () => {
  const { searchQueryState } = useContext(SearchFilterContext);

  // Extract the specific values we need as primitives
  const search = searchQueryState?.search;
  const options = search?.options;
  const childAgesLength = options?.childAges?.length ?? 0;
  const rateRequestTypesLength = options?.rateRequestTypes?.length ?? 0;
  const includeTaxesAndFees = searchQueryState?.includeTaxesAndFees ?? false;

  // Return memoized object with stable primitive dependencies
  return useMemo(
    () => ({
      searchQueryState, // Still need full object for price formatting functions
      childCount: childAgesLength,
      rateRequestTypesLength,
      includeTaxesAndFees,
    }),
    // Only re-compute when these specific values change
    [searchQueryState, childAgesLength, rateRequestTypesLength, includeTaxesAndFees]
  );
};

// Labels matching shop's jsonContent structure
const LABELS = {
  openingSoonLabel: 'Opening Soon',
  rateUnavailableLabel: 'Rate Unavailable',
  unavailableLabel: 'Unavailable',
  pointsLabel: 'pts',
  currencyLabel: {},
};

// List of labels that indicate unavailable rates (used to determine return type)
const UNAVAILABLE_LABELS = [LABELS.openingSoonLabel, LABELS.rateUnavailableLabel, LABELS.unavailableLabel, '.'];

/**
 * Adapter function that wraps shop's getLabelText and converts the string result
 * to the object format expected by ChatMapView.
 *
 * For chat, we pass:
 * - isFlexibleDateSearch: false (not applicable)
 * - flexibleOrManyProperty: false (not applicable)
 * - isShowFullPrice: from searchQueryState.includeTaxesAndFees (respects user's filter selection)
 */
const getMarkerLabelFromShop = (
  edge: any,
  searchQueryState: any,
  isSpecialRateSearch: boolean,
  childCount: number,
  noRatesAreAvailable: boolean,
  isErrorState: boolean,
  currentLocale: string,
  isSinglePropertyResponse: boolean,
  isShowFullPrice: boolean
): { priceLabel?: string; rateUnavailableLabel?: string } => {
  const labelText = getLabelText(
    edge,
    false, // isFlexibleDateSearch - not applicable for chat
    searchQueryState,
    isSpecialRateSearch,
    searchQueryState?.search?.options ?? {},
    LABELS,
    false, // flexibleOrManyProperty - not applicable for chat
    isErrorState,
    childCount,
    currentLocale,
    isShowFullPrice,
    noRatesAreAvailable,
    isSinglePropertyResponse
  );

  // Convert string result to object format for ChatMapView
  if (UNAVAILABLE_LABELS.includes(labelText)) {
    return { rateUnavailableLabel: labelText };
  }
  return { priceLabel: labelText };
};

/**
 * Props interface for ChatMapViewContainer
 */
interface ChatMapViewContainerProps {
  onOpenHotelPanel?: (propertyData: any, propertyId: string, options?: { messageViewData?: any; openGallery?: boolean }) => void;
}

/**
 * Container that bridges shop React contexts to the prop-driven ChatMapView.
 * Sits at the same swap point where ChatGoogleMapContainer was used (mapView/index.tsx).
 * Reads marker data from SearchResultContext.searchResult.edges and passes it
 * as typed props to ChatMapView.
 *
 * Performance optimizations:
 * - Wrapped with React.memo to prevent re-renders from parent component updates
 * - Uses custom hooks to selectively consume only needed context values
 * - Prevents re-renders when unrelated context values change
 * - Uses useRef for stable callback references
 */
const ChatMapViewContainerComponent: React.FC<ChatMapViewContainerProps> = ({ onOpenHotelPanel }) => {
  // Use custom hooks for selective context consumption
  const {
    isStaticMap,
    isMapLoading,
    isErrorState,
    isSpecialRateSearch: contextIsSpecialRateSearch,
  } = useMapRenderedData();
  const edges = useSearchResultEdges();
  const hoveredHotelId = useHoveredHotelId();
  const isSinglePropertyResponse = useIsSinglePropertyResponse();
  const { searchQueryState, childCount, rateRequestTypesLength, includeTaxesAndFees } = useSearchQueryValues();
  const { currentLocale } = usePageContext();

  // Get isNewSearchResult flag for map animation
  const isNewSearchResult = useMarriottAIPreviewStore(state => state.isNewSearchResult);
  const setIsNewSearchResult = useMarriottAIPreviewStore(state => state.setIsNewSearchResult);

  // Get noRatesAreAvailable from store (same as shop)
  const noRatesAreAvailable =
    useSearchResultsStore((state: { noRatesAreAvailable: any }) => state.noRatesAreAvailable)?.noRatesAreAvailable
      ?.noRatesAvailableForAnyProperty ?? false;

  // Use context value if available, otherwise calculate from options
  const isSpecialRateSearch = contextIsSpecialRateSearch ?? rateRequestTypesLength > 1;

  const markers: ChatMapMarker[] = useMemo(
    () =>
      edges
        .map((edge: any) => {
          const property = edge?.node?.property;
          if (!property) return null;
          const lat = property.basicInformation?.latitude;
          const lng = property.basicInformation?.longitude;
          if (!lat || !lng) return null;

          // Get marker label using shop's getLabelText function
          const { priceLabel, rateUnavailableLabel } = getMarkerLabelFromShop(
            edge,
            searchQueryState,
            isSpecialRateSearch,
            childCount,
            noRatesAreAvailable,
            isErrorState,
            currentLocale ?? 'en-US',
            isSinglePropertyResponse,
            includeTaxesAndFees
          );

          return {
            propertyId: property.id,
            latitude: lat,
            longitude: lng,
            name: property.basicInformation?.nameInDefaultLanguage ?? '',
            imageUrl: property.media?.primaryImage?.edges?.[0]?.node?.imageUrls?.wideHorizontal,
            ratingValue: property.reviews?.stars?.count,
            reviewCount: property.reviews?.numberOfReviews?.count,
            distance: edge.node.distance,
            property,
            priceLabel,
            isAIRecommended: edge?.node?.isBestMatch ?? false,
            rateUnavailableLabel,
            edge,
          };
        })
        .filter(Boolean) as ChatMapMarker[],
    [
      edges,
      searchQueryState,
      isSpecialRateSearch,
      childCount,
      noRatesAreAvailable,
      isErrorState,
      currentLocale,
      isSinglePropertyResponse,
      includeTaxesAndFees,
    ]
  );

  // Use ref to keep callback stable across renders
  const onOpenHotelPanelRef = useRef(onOpenHotelPanel);
  onOpenHotelPanelRef.current = onOpenHotelPanel;

  // Stable callback that doesn't change reference
  // Don't pass messageViewData - map view should use global search criteria for URLs
  const handleMarkerClick = useCallback((propertyData: any, propertyId: string, options?: { openGallery?: boolean }) => {
    onOpenHotelPanelRef.current?.(propertyData, propertyId, options);
  }, []);

  const handleHotelHover = useCallback((_hotelId: string | null) => {
    // Hover sync is handled by eventUtil inside ChatMapView
  }, []);

  return (
    <ChatMapView
      markers={markers}
      hoveredHotelId={hoveredHotelId}
      onMarkerClick={handleMarkerClick}
      onHotelHover={handleHotelHover}
      isStaticMap={isStaticMap}
      isMapLoading={isMapLoading}
      isNewSearchResult={isNewSearchResult}
      onAnimationComplete={() => setIsNewSearchResult(false)}
      isSinglePropertyResponse={isSinglePropertyResponse}
    />
  );
};

// /**
//  * Custom comparison function for React.memo.
//  * Since we use useRef to handle the onOpenHotelPanel callback internally,
//  * we can safely skip re-renders when only the callback reference changes.
//  * The component will still re-render when context values change (via hooks).
//  */
// const arePropsEqual = (_prevProps: ChatMapViewContainerProps, _nextProps: ChatMapViewContainerProps): boolean => {
//   // Always return true to skip prop-based re-renders.
//   // The callback is handled via useRef, so we don't need to re-render when it changes.
//   // Context changes will still trigger re-renders through the custom hooks.
//   return true;
// };

const ChatMapViewContainer = React.memo(ChatMapViewContainerComponent);

export default ChatMapViewContainer;
